import type { ReactElement } from "react";
import { ScoreboardViewMode } from "@ppb/the-wall-common/types";
import EventHeader from "@ppb/tbd-components-rich-data/components/EventHeader/view/EventHeader.web";
import FootballFixture from "@ppb/tbd-components-rich-data/components/FootballFixture/view/FootballFixture.web";
import FootballFixturePlaceholder from "@ppb/tbd-components-rich-data/components/FootballFixture/view/FootballFixturePlaceholder.web";
import { SPORT_ID } from "../../event-view-sports";
import type { RenderableFixture } from "../../viewmodel/EventView.viewmodel";

/**
 * A sport's scoreboard, rendered inside the header's own surface. Render functions, not components, so
 * the slot adds no node to the element tree. `fixture` is the viewmodel's `renderableFixtureOf` output.
 * Contract: `docs/specs/event-view-apollo-all-sports.md` §4.6.
 */
export type ScoreboardSlot = {
  renderDefault: (fixture: RenderableFixture | undefined) => ReactElement;
  // The compact copy `StickyHeader` pins into the page header on scroll.
  renderSmall: (fixture: RenderableFixture | undefined) => ReactElement;
  renderPlaceholder: () => ReactElement;
};

const renderFootball = (fixture: RenderableFixture | undefined, isSticky: boolean): ReactElement => {
  if (!fixture) return <FootballFixturePlaceholder />;

  const viewMode = isSticky ? ScoreboardViewMode.SMALL : ScoreboardViewMode.DEFAULT;

  return fixture.kind === "football" ? (
    <FootballFixture urn={fixture.urn} viewMode={viewMode} />
  ) : (
    <EventHeader urn={fixture.urn} viewMode={viewMode} isSticky={isSticky} showTertiaryTitle={!isSticky} />
  );
};

const FOOTBALL_SLOT: ScoreboardSlot = {
  renderDefault: (fixture) => renderFootball(fixture, false),
  renderSmall: (fixture) => renderFootball(fixture, true),
  renderPlaceholder: () => <FootballFixturePlaceholder />,
};

export const SCOREBOARD_SLOTS: Readonly<Partial<Record<number, ScoreboardSlot>>> = {
  [SPORT_ID.FOOTBALL]: FOOTBALL_SLOT,
};
